/**
 * COMMENT UNE COMPÉTENCE SE VALIDE — module PUR.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Quatre modalités, combinables : observation directe, questionnaire
 * théorique, mise en situation, vérification documentaire. Une ligne de grille
 * porte celles qui s'appliquent — « OD + QT » : on observe le geste ET on
 * vérifie que la théorie est là.
 *
 * ── Pourquoi ce module existe (12/09/2026) ─────────────────────────────────
 *
 * « Liste déroulante pour les modalités de validation. » — RQ, 11/09.
 *
 * Le champ était un `<input>` libre, avec `ex. OD + QT` en indication. Ce que
 * les 399 lignes du registre en ont fait, mesuré en base :
 *
 *     OD + QT   34     QT + OD    3
 *     OD + VD   31     VD + OD    1
 *     QT + VD    9     VD + QT    1
 *     MS + VD    6     VD + MS    1
 *
 * ⚠️ SIX LIGNES SONT MAL RANGÉES, et le défaut n'est pas d'orthographe : « QT +
 * OD » et « OD + QT » disent exactement la même chose, mais ce sont deux
 * chaînes différentes. Tout ce qui groupe, compte ou filtre sur cette colonne
 * voit deux modalités là où il n'y en a qu'une. C'est le même mécanisme que les
 * 54 graphies de salle et les 78 sections de tâche : un champ libre sur un
 * vocabulaire fermé dérive, toujours, et la dérive ne se voit qu'en base.
 *
 * 38 lignes de plus sont VIDES — et celles-là restent vides. Le document
 * d'origine ne dit pas comment elles se valident ; leur inventer une modalité
 * serait écrire à la place du laboratoire.
 *
 * ── L'ORDRE FAIT PARTIE DE LA VALEUR ───────────────────────────────────────
 *
 * `ecrire` rend toujours les codes dans l'ordre du vocabulaire, jamais dans
 * celui où l'on a cliqué. Sans cela, la liste déroulante produirait à son tour
 * ses propres doublons — on aurait remplacé une dérive par une autre, en
 * croyant l'avoir fermée.
 */

/** Les quatre modalités, DANS L'ORDRE qui fait foi à l'écriture. */
export const MODALITES: { code: string; libelle: string }[] = [
  { code: 'OD', libelle: 'Observation directe en situation réelle' },
  { code: 'QT', libelle: 'Questionnaire théorique (oral ou écrit)' },
  { code: 'MS', libelle: 'Mise en situation, cas simulé' },
  { code: 'VD', libelle: 'Vérification documentaire des enregistrements remplis' },
];

/** Compatibilité : la forme `Record` qu'employait l'écran avant l'extraction. */
export const METHODES: Record<string, string> = Object.fromEntries(
  MODALITES.map((m) => [m.code, m.libelle]),
);

const RANG = new Map(MODALITES.map((m, i) => [m.code, i]));

/**
 * « QT + OD » → `['OD','QT']`.
 *
 * ⚠️ NE JETTE RIEN. Un code inconnu — il en existe au registre, saisis à la
 * main — est CONSERVÉ, rangé après les quatre connus. Les écarter « pour faire
 * propre » ferait disparaître, à la première réouverture d'une ligne, une
 * donnée que personne n'a décidé de supprimer : la ligne se rouvre, se
 * réenregistre, et la valeur est perdue sans un mot.
 *
 * Le séparateur est le `+`, mais la virgule et le point-virgule sont acceptés :
 * ce sont les trois formes qu'on trouve en base.
 */
export function lireModalites(texte: string | null | undefined): string[] {
  const bruts = String(texte ?? '')
    .split(/[+,;]/)
    .map((x) => x.trim().toUpperCase())
    .filter(Boolean);
  /* Dédoublonné : « OD + OD » existe, et rendrait deux pastilles identiques. */
  const uniques = [...new Set(bruts)];
  return uniques.sort((a, b) => (RANG.get(a) ?? 99) - (RANG.get(b) ?? 99)
    || a.localeCompare(b));
}

/**
 * `['QT','OD']` → « OD + QT ».
 *
 * ⚠️ C'est ICI que se ferme la dérive : quel que soit l'ordre des clics, la
 * chaîne écrite est la même. Une liste déroulante qui écrirait dans l'ordre de
 * sélection recréerait les six lignes mal rangées qu'elle vient corriger.
 *
 * Rend `''` sur une liste vide, et non « — » ni « aucune » : la colonne vide a
 * un sens (« le document ne le dit pas »), que l'écran affiche à sa façon.
 */
export function ecrireModalites(codes: string[]): string {
  return lireModalites(codes.join('+')).join(' + ');
}

/** La modalité est-elle du vocabulaire, ou une saisie d'origine à relire ? */
export function modaliteConnue(code: string): boolean {
  return RANG.has(String(code || '').trim().toUpperCase());
}
